import type { Header } from '@tanstack/react-table';
import * as React from 'react';

import HeaderGroupItem from './HeaderGroupItem';
import { getHeaderLeafIds, getHeaderRowRange } from './headerUtil';
import SimpleHeaderRow from './SimpleHeaderRow';
import styles from './styles.module.scss';
import type { CalculatedHeaderData } from './types';

interface OwnProps<T> {
	gridTemplateColumns: string;
	headerRef: React.Ref<Nullable<HTMLDivElement>>;
	onColumnDrag: (header: string) => (e: React.MouseEvent<Element, MouseEvent>) => void;
	setHeaderReference: (headerId: string) => (_ref: HTMLDivElement) => void;
	fixedLeafColumnStyles: Record<string, React.CSSProperties>;
	headerData: CalculatedHeaderData<T>;
}

type Props<T> = OwnProps<T>;

export type HeaderLayoutEntry = {
	columnStart: number;
	columnEnd: number;
	rowStart: number;
	rowEnd: number;
	leafIds: string[];
};

type PrecomputedLayout<T> = {
	headerLayout: Map<string, HeaderLayoutEntry>;
	/** Bucket: key = columnStart of the header, value = headers that START here */
	startIndex: Map<number, Array<{ headerId: string; rowStart: number; rowEnd: number; }>>;
	allHeaders: Header<T, unknown>[];
	maxDepth: number;
};

function HeaderGroups<T>(props: Props<T>) {
	const {
		gridTemplateColumns,
		headerRef,
		onColumnDrag,
		setHeaderReference,
		fixedLeafColumnStyles,
		headerData,
	} = props;

	const { headerGroups, leafColumns } = headerData;

	const leafIds = React.useMemo(() => leafColumns.map((col) => col.id), [leafColumns]);
	const leafIdIndex = React.useMemo(() => new Map(leafIds.map((id, idx) => [id, idx])), [leafIds]);

	// Precompute layout once — O(N) instead of O(N²·L)
	const precomputed = React.useMemo<PrecomputedLayout<T>>(() => {
		const allNonPlaceholderHeaders = headerGroups
			.flatMap((headerGroup) => headerGroup.headers)
			.filter((header) => !header.isPlaceholder);

		const rowByHeaderId = headerGroups.reduce<Record<string, number>>((acc, group, groupIndex) => {
			group.headers.forEach((header) => {
				acc[header.id] = groupIndex + 1;
			});
			return acc;
		}, {});

		const headerLayout = new Map<string, HeaderLayoutEntry>();
		const startIndex = new Map<number, Array<{ headerId: string; rowStart: number; rowEnd: number; }>>();

		for (const header of allNonPlaceholderHeaders) {
			const headerLeafIds = getHeaderLeafIds(header, new Set(leafIds));
			if (!headerLeafIds.length) {
				continue;
			}

			const { rowStart, rowEnd } = getHeaderRowRange(header, rowByHeaderId, headerGroups.length);

			const firstLeafIndex = leafIdIndex.get(headerLeafIds[0]);
			if (firstLeafIndex === undefined) {
				continue;
			}
			const columnStart = firstLeafIndex + 1;
			const columnEnd = columnStart + headerLeafIds.length;

			headerLayout.set(header.id, { columnStart, columnEnd, rowStart, rowEnd, leafIds: headerLeafIds });

			if (!startIndex.has(columnStart)) {
				startIndex.set(columnStart, []);
			}
			startIndex.get(columnStart)!.push({ headerId: header.id, rowStart, rowEnd });
		}

		return { headerLayout, startIndex, allHeaders: allNonPlaceholderHeaders, maxDepth: headerGroups.length };
	}, [headerGroups, leafIds, leafIdIndex]);

	const renderHeader = React.useCallback((header: Header<T, unknown>) => {
		const layout = precomputed.headerLayout.get(header.id);
		if (!layout) {
			return null;
		}

		return (
			<HeaderGroupItem<T>
				boundaryIndex={precomputed.startIndex}
				columnStart={layout.columnStart}
				fixedLeafColumnStyles={fixedLeafColumnStyles}
				header={header}
				headerLayout={precomputed.headerLayout}
				headerLeafIds={layout.leafIds}
				leafIdIndex={leafIdIndex}
				leafIds={leafIds}
				maxDepth={precomputed.maxDepth}
				onColumnDrag={onColumnDrag}
				setHeaderReference={setHeaderReference}
				totalLeafCount={leafIds.length}
			/>
		);
	}, [
		precomputed.headerLayout,
		precomputed.startIndex,
		fixedLeafColumnStyles,
		leafIdIndex,
		leafIds,
		onColumnDrag,
		setHeaderReference,
		precomputed.maxDepth,
	]);

	if (precomputed.maxDepth <= 1) {
		return (
			<SimpleHeaderRow<T>
				gridTemplateColumns={gridTemplateColumns}
				headerData={headerData}
				headerRef={headerRef}
				onColumnDrag={onColumnDrag}
				setHeaderReference={setHeaderReference}
			/>
		);
	}

	return (
		<div
			className={styles['simple-table__header-grid']}
			ref={headerRef}
			role="rowgroup"
			style={{
				gridTemplateColumns,
				gridTemplateRows: `repeat(${precomputed.maxDepth}, minmax(26px, auto))`,
			}}
		>
			{precomputed.allHeaders.map(renderHeader)}
		</div>
	);
}

export default React.memo(HeaderGroups) as typeof HeaderGroups;
